import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function StoryModal({ story, onClose }) {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setActiveIdx((prev) => (prev + 1) % (story.gallery?.length || 1));
      if (e.key === "ArrowLeft") setActiveIdx((prev) => (prev - 1 + (story.gallery?.length || 1)) % (story.gallery?.length || 1));
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIdx, story]);

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#101810]/95 backdrop-blur-md transition-opacity">
      <div className="absolute top-0 left-0 right-0 h-20 px-6 md:px-12 flex items-center justify-between z-20 border-b border-[#283828]/50">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#C9D1B5] font-mono">
            {story.location} · {story.date}
          </span>
          <h3 className="font-serif text-xl md:text-2xl text-white font-medium">{story.couple}</h3>
        </div>
        <button onClick={onClose} className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white hover:text-[#182418] transition-colors">
          <X size={20} strokeWidth={1.5} />
        </button>
      </div>

      <div className="relative w-full h-[65vh] max-w-5xl px-6 flex items-center justify-center mt-8">
        <img src={story.gallery?.[activeIdx]} alt="" className="max-h-full max-w-full object-contain shadow-2xl select-none" />
      </div>

      <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-3 px-6 overflow-x-auto py-2">
        {story.gallery?.map((imgUrl, idx) => (
          <button key={idx} onClick={() => setActiveIdx(idx)} className={`w-14 h-14 md:w-16 md:h-16 flex-shrink-0 overflow-hidden border-2 transition-all ${activeIdx === idx ? "border-[#C9D1B5] scale-105" : "border-transparent opacity-40 hover:opacity-100"}`}>
            <img src={imgUrl} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function WeddingStoriesSection() {
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);

  useEffect(() => {
    api.get("/wedding-stories")
      .then(({ data }) => setStories(Array.isArray(data) ? data : []))
      .catch(() => setStories([]));
  }, []);

  if (stories.length === 0) return null;

  return (
    <section className="py-20 md:py-28 bg-[#FAF9F5] border-y border-olive-soft relative overflow-hidden">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <span className="text-[10px] uppercase tracking-[0.35em] text-primary font-semibold flex items-center gap-2 mb-2">
              <span className="w-6 h-[1px] bg-primary" /> Curated Archives
            </span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground font-normal tracking-tight">
              Full wedding <span className="italic font-serif text-primary">stories.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {stories.map((story) => (
            <div
              key={story.id}
              onClick={() => setSelectedStory(story)}
              className="group relative h-[220px] sm:h-[420px] md:h-[450px] cursor-pointer overflow-hidden bg-[#141F14] rounded-[2px] shadow-lg"
            >
              <img src={story.cover} alt={story.couple} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e160e]/95 via-[#0e160e]/40 to-transparent opacity-80" />
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-7 z-10 flex flex-col justify-end">
                <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#C9D1B5] font-mono mb-1 sm:mb-1.5">{story.location} · {story.count || "Gallery"}</span>
                <h3 className="font-serif text-base sm:text-2xl md:text-[26px] text-white font-medium leading-tight">{story.couple}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedStory && (
        <StoryModal story={selectedStory} onClose={() => setSelectedStory(null)} />
      )}
    </section>
  );
}